"use client";

import React from "react";
import Link from "next/link";
import Image from "next/image";
import { SbBlokData, storyblokEditable } from "@storyblok/react";
import type {
  FooterBottom,
  FooterCompanyDetails,
  FooterContact,
  FooterLink,
  Footer,
} from "@/.storyblok/types/319751/storyblok-components";
import Social from "@/components/layouts/Social";

interface FooterProps {
  blok?: Footer & SbBlokData;
}

const FooterComponent: React.FC<FooterProps> = ({ blok }) => {
  if (!blok) return null;

  return (
    <footer
      {...storyblokEditable(blok)}
      id="page-footer"
      className="pt-4 pt-md-6 pt-lg-5 pt-xl-6"
    >
      <div className="container mt-3 mt-lg-5">
        <div className="row">
          {/* Contact Section */}
          {blok.contact_section?.[0] && (
            <FooterContact contact={blok.contact_section[0]} />
          )}

          {/* Navigation Sections */}
          {blok.navigation_sections && (
            <div className="col-24 col-md-14 col-lg-18 col-xxl-16 footer-links">
              <div className="row">
                {/* First column with two sections */}
                <div className="col-24 col-sm-12 col-lg-6">
                  {blok.navigation_sections.slice(0, 2).map((section) => (
                    <div key={section._uid} className="accordion-item mb-sm-3">
                      <h3 className="accordion-header text-white h5 mb-0 mb-sm-2">
                        <button
                          className="accordion-button"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target={`#collapse-${section._uid}`}
                          aria-expanded="false"
                          aria-controls={`collapse-${section._uid}`}
                        >
                          {section.title}
                        </button>
                      </h3>
                      <div
                        className="accordion-collapse collapse"
                        id={`collapse-${section._uid}`}
                      >
                        <ul className="list-unstyled mb-2">
                          {section.links?.map((link) => (
                            <FooterLink key={link._uid} link={link as FooterLink & SbBlokData} />
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Remaining sections */}
                {blok.navigation_sections.slice(2).map((section) => (
                  <div key={section._uid} className="col-24 col-sm-12 col-lg-6">
                    <div className="accordion-item mb-sm-3">
                      <h3 className="accordion-header text-white h5 mb-0 mb-sm-2">
                        <button
                          className="accordion-button"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target={`#collapse-${section._uid}`}
                          aria-expanded="false"
                          aria-controls={`collapse-${section._uid}`}
                        >
                          {section.title}
                        </button>
                      </h3>
                      <div
                        className="accordion-collapse collapse"
                        id={`collapse-${section._uid}`}
                      >
                        <ul className="list-unstyled mb-2">
                          {section.links?.map((link) => (
                            <FooterLink key={link._uid} link={link as FooterLink & SbBlokData} />
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Bottom Section */}
        {blok.bottom_section?.[0] && (
          <FooterBottom bottom={blok.bottom_section[0]} />
        )}
      </div>
    </footer>
  );
};

const FooterContact: React.FC<{ contact: FooterContact }> = ({
  contact,
}) => {
  return (
    <div className="col-24 col-md-10 col-lg-6 col-xxl-8 mb-3 mb-sm-4 mb-md-0">
      {contact.logo && (
        <Link href="/" className="footer-brand mb-3 mb-lg-0" aria-label="Bulletproof">
          <Image
            src={contact.logo.filename || ""}
            alt={contact.logo.alt || ""}
            width={112}
            height={561}
            className="w-auto"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            loading="lazy"
            quality={75}
          />
        </Link>
      )}
      <h2 className="visually-hidden">Contact</h2>
      <dl className="mb-3">
        {contact.address && (
          <>
            <dt className="visually-hidden">Address</dt>
            <dd className="mb-3">
              <address
                dangerouslySetInnerHTML={{
                  __html: contact.address.replace(/\n/g, "<br />"),
                }}
              />
            </dd>
          </>
        )}

        {contact.phone && (
          <>
            <dt className="visually-hidden">Telephone</dt>
            <dd className="mb-0">
              {contact.phone_link ? (
                <Link href={contact.phone_link.cached_url}>
                  {contact.phone}
                </Link>
              ) : (
                contact.phone
              )}
            </dd>
          </>
        )}

        {contact.email && (
          <>
            <dt className="visually-hidden">Email</dt>
            <dd>
              {contact.email_link ? (
                <Link href={contact.email_link.cached_url}>
                  {contact.email}
                </Link>
              ) : (
                contact.email
              )}
            </dd>
          </>
        )}
      </dl>

      {contact.cta_button && contact.cta_button_link && (
        <p className="mb-3 mb-xl-4">
          <Link
            href={contact.cta_button_link.cached_url}
            className="btn btn-primary"
          >
            {contact.cta_button}
          </Link>
        </p>
      )}

      {contact.socials && (
        <Social color="white" social_links={contact.socials} />
      )}
    </div>
  );
};

// const FooterNavSection: React.FC<{ section: FooterNavSectionStoryblok }> = ({ section }) => {
//     return (
//         <div className="col-24 col-sm-12 col-lg-6">
//             <div {...storyblokEditable(section)} className="accordion-item mb-sm-3">
//                 <h3 className="accordion-header text-white h5 mb-0 mb-sm-2">
//                     <button
//                         className="accordion-button"
//                         type="button"
//                         data-bs-toggle="collapse"
//                         data-bs-target={`#collapse-${section._uid}`}
//                         aria-expanded="false"
//                     >
//                         {section.title}
//                     </button>
//                 </h3>
//                 <div className="accordion-collapse collapse" id={`collapse-${section._uid}`}>
//                     <ul className="list-unstyled mb-2">
//                         {section.links?.map((link) => (
//                             <FooterLink key={link._uid} link={link} />
//                         ))}
//                     </ul>
//                 </div>
//             </div>
//         </div>
//     );
// };

const FooterLink: React.FC<{
  link: FooterLink & SbBlokData;
  className?: string;
}> = ({ link, className = "" }) => {
  if (!link.text) return null;

  return (
    <li {...storyblokEditable(link)} className={className}>
      {link.link?.cached_url ? (
        <Link
          href={link.link.cached_url}
          {...(link.external
            ? { target: "_blank", rel: "noopener noreferrer" }
            : {})}
        >
          {link.text}
        </Link>
      ) : (
        <span>{link.text}</span>
      )}
    </li>
  );
};

const FooterBottom: React.FC<{ bottom: FooterBottom }> = ({
  bottom,
}) => {
  return (
    <div className="row footer-copyright mt-sm-3 mt-lg-4 mt-xl-4 py-3 justify-content-between text-center text-md-start">
      <div className="col-24 col-lg-auto">
        <p className="mb-1">
          &copy; {new Date().getFullYear()}{" "}
          <Link href="/" className="text-white">
            Bulletproof
          </Link>
          . All rights reserved.
        </p>
        {bottom.legal_links && bottom.legal_links.length > 0 && (
          <ul className="list-unstyled list-inline small">
            {bottom.legal_links.map((link) => (
              <FooterLink key={link._uid} link={link as FooterLink & SbBlokData} className="me-2" />
            ))}
          </ul>
        )}
      </div>
      {bottom.company_details?.[0] && (
        <CompanyDetails details={bottom.company_details[0]} />
      )}
    </div>
  );
};

const CompanyDetails: React.FC<{ details: FooterCompanyDetails }> = ({
  details,
}) => {
  return (
    <div className="col-24 col-lg-auto text-lg-end">
      {details.trading_name && <p className="mb-1">{details.trading_name}</p>}
      {details.company_number && details.vat_number && (
        <p className="small">
          Company number {details.company_number} &bull; VAT No:{" "}
          {details.vat_number}
        </p>
      )}
    </div>
  );
};

export default FooterComponent;